import { useEffect, useState } from "react";
import type { QualificationVerdict } from "@/api/buyers";
import { extractErrorMessage } from "@/api/client";
import { Button, Textarea, useToast } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { VERDICTS, buyerLabel } from "../buyerFilters";
import { useOverrideInterviewVerdict } from "./useBuyerInterview";

/**
 * Disagree with the computed roll-up, on the record.
 *
 * <p>🔴 <b>The reason is required, and the computed verdict is never
 * overwritten.</b> A verdict that disagrees with the answers behind it is a
 * judgement someone made, and the reason is the only record of it. The server
 * enforces this too — the check here is the difference between being told what
 * is missing beside the field and being told "400" after a round trip.</p>
 */
export function OverrideVerdictModal({
  buyerLeadId,
  open,
  computedVerdict,
  currentVerdict,
  currentReason,
  onClose,
}: {
  buyerLeadId: string;
  open: boolean;
  computedVerdict: QualificationVerdict;
  currentVerdict: QualificationVerdict;
  currentReason?: string | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const override = useOverrideInterviewVerdict(buyerLeadId);

  const [verdict, setVerdict] = useState<QualificationVerdict>(currentVerdict);
  const [reason, setReason] = useState(currentReason ?? "");
  const [showReasonError, setShowReasonError] = useState(false);

  // Rehydrate each time it opens, so "open → save unchanged" round-trips the
  // same values and a stale local edit can never be submitted against a record
  // that has since moved.
  useEffect(() => {
    if (!open) return;
    setVerdict(currentVerdict);
    setReason(currentReason ?? "");
    setShowReasonError(false);
  }, [open, currentVerdict, currentReason]);

  const reasonMissing = reason.trim().length === 0;

  const submit = () => {
    if (reasonMissing) {
      setShowReasonError(true);
      return;
    }
    override.mutate(
      { verdict, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success("Verdict overridden.");
          onClose();
        },
        onError: (error: unknown) => toast.error(extractErrorMessage(error)),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Override the assessment"
      description={`The interview computed ${buyerLabel(computedVerdict)}. The computed value is kept alongside your override.`}
      size="sm"
      footer={
        <>
          <Button variant="text" onClick={onClose} disabled={override.isPending}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            loading={override.isPending}
            disabled={override.isPending}
            data-testid="override-save"
          >
            Record override
          </Button>
        </>
      }
    >
      <div className="grid gap-space-3">
        <div>
          <label
            htmlFor="override-verdict"
            className="block text-caption uppercase tracking-wide text-gray-mid mb-space-1"
          >
            Verdict
          </label>
          <Select
            id="override-verdict"
            value={verdict}
            onChange={(e) => setVerdict(e.target.value as QualificationVerdict)}
          >
            {VERDICTS.map((v) => (
              <option key={v} value={v}>
                {buyerLabel(v)}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <label
            htmlFor="override-reason"
            className="block text-caption uppercase tracking-wide text-gray-mid mb-space-1"
          >
            Why — required
          </label>
          <Textarea
            id="override-reason"
            rows={3}
            value={reason}
            invalid={showReasonError && reasonMissing}
            onChange={(e) => setReason(e.target.value)}
          />
          {showReasonError && reasonMissing && (
            <p role="alert" className="text-caption text-status-error mt-space-1 m-0">
              An override needs a reason — it is the only record of the judgement.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
